import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { translator } from './format';
import type { Translate } from './format';
import { en } from './en';
import type { Messages } from './en';
import { es } from './es';
import {
  DEFAULT_UI_LOCALE,
  DEVICE_LOCALE,
  UI_LOCALES,
  browserLocales,
  narrowUiLocale,
} from './locale';
import type { UiLocale } from './locale';

/**
 * The catalogs, the one function a component uses, and the one a module does.
 *
 * Both languages are in the bundle. That is a deliberate non-decision: the two
 * catalogs together are a few kilobytes gzipped, and code-splitting them would
 * buy a round trip on an installed PWA whose whole promise is that it opens in a
 * tunnel (spec §8). When there are six languages this is worth revisiting; at
 * two it is not.
 *
 * ## Two ways in, and they cannot disagree
 *
 * `deviceT` is for everything that is not a component — `mirror/time.ts`'s
 * formatters, `actions.ts`'s intent descriptions, `outbox/tasks.ts`'s sentences.
 * `useT()` is for components. Both resolve to `DEVICE_LOCALE` by default, which
 * is why the context's *default value* is the device catalog rather than `null`:
 * a component works with no provider above it, so nothing had to be wrapped for
 * this wave and the existing suites render exactly as they did.
 *
 * `<MessagesProvider locale="es">` overrides the tree below it. Today that is
 * what `ui/spanish.test.tsx` uses; tomorrow it is where a per-staff-member
 * language preference plugs in, with `App` reading it and passing it here.
 */

const CATALOGS: Record<UiLocale, Messages> = { en, es };

export type { Messages, Translate, UiLocale };
export { DEFAULT_UI_LOCALE, DEVICE_LOCALE, UI_LOCALES, browserLocales, narrowUiLocale };

export function catalogFor(locale: UiLocale): Messages {
  return CATALOGS[locale];
}

/** A bound `t` for one language. */
export function translatorFor(locale: UiLocale): Translate<Messages> {
  return translator(CATALOGS[locale]);
}

/** The type a component prop, a context field or an options bag uses to carry `t`. */
export type T = Translate<Messages>;

/** The catalog this device reads, decided once at module load (`locale.ts`). */
export function deviceCatalog(): Messages {
  return CATALOGS[DEVICE_LOCALE];
}

/**
 * The device's `t`, bound once.
 *
 * A constant rather than a function call, because it is the default value of a
 * dozen optional parameters and of the React context — and a default that
 * allocated a new closure per evaluation would make `useMemo` dependencies churn
 * for no reason.
 */
export const deviceT: T = translator(CATALOGS[DEVICE_LOCALE]);

interface MessagesValue {
  locale: UiLocale;
  t: T;
}

const MessagesContext = createContext<MessagesValue>({ locale: DEVICE_LOCALE, t: deviceT });

export function MessagesProvider({
  locale,
  children,
}: {
  locale: UiLocale;
  children: ReactNode;
}): ReactNode {
  const value = useMemo<MessagesValue>(() => ({ locale, t: translatorFor(locale) }), [locale]);
  return <MessagesContext.Provider value={value}>{children}</MessagesContext.Provider>;
}

export function useT(): T {
  return useContext(MessagesContext).t;
}

/**
 * The same language, for `Intl`.
 *
 * Components that format a date, a clock or an amount pass this to
 * `mirror/time.ts` rather than reaching for `DEVICE_LOCALE`, so a provider moves
 * the words and the numbers together. One value, one context, no way for a
 * Spanish sentence to carry an English date.
 */
export function useLocale(): UiLocale {
  return useContext(MessagesContext).locale;
}
